import {
  Button,
  ConfirmDialog,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  type useConfirm,
} from '@estac1/ui';
import type { UseMutationResult } from '@tanstack/react-query';
import { Calendar } from 'lucide-react';
import {
  moduleToggleConfirmDescription,
  moduleToggleLabel,
  type PendingModuleToggle,
  type ToggleableModuleKey,
} from './lot-module-toggles.js';
import type { Lot } from './types.js';

interface ClientDialogsProps {
  extending: { lotId: string; lotName: string } | null;
  setExtending: (v: { lotId: string; lotName: string } | null) => void;
  extraDays: number;
  setExtraDays: (v: number) => void;
  extendTrial: Pick<
    UseMutationResult<{ trialEndsAt: string }, unknown, { lotId: string; extraDays: number }>,
    'isPending' | 'mutate'
  >;
  archiveConfirm: ReturnType<typeof useConfirm>;
  unarchiveConfirm: ReturnType<typeof useConfirm>;
  moduleToggleConfirm: ReturnType<typeof useConfirm>;
  resetCheckoutConfirm: ReturnType<typeof useConfirm>;
  pendingArchive: Lot | null;
  pendingUnarchive: Lot | null;
  pendingResetCheckout: Lot | null;
  pendingModuleToggle: PendingModuleToggle | null;
  archive: Pick<UseMutationResult<{ archivedAt: string }, unknown, string>, 'isPending'>;
  unarchive: Pick<UseMutationResult<{ archivedAt: null }, unknown, string>, 'isPending'>;
  resetCheckout: Pick<UseMutationResult<{ ok: true }, unknown, string>, 'isPending'>;
  toggleModule: Pick<
    UseMutationResult<
      unknown,
      unknown,
      { lotId: string; key: ToggleableModuleKey; enabled: boolean }
    >,
    'isPending'
  >;
}

export const ClientDialogs = ({
  extending,
  setExtending,
  extraDays,
  setExtraDays,
  extendTrial,
  archiveConfirm,
  unarchiveConfirm,
  moduleToggleConfirm,
  resetCheckoutConfirm,
  pendingArchive,
  pendingUnarchive,
  pendingResetCheckout,
  pendingModuleToggle,
  archive,
  unarchive,
  resetCheckout,
  toggleModule,
}: ClientDialogsProps) => {
  const modLabel = pendingModuleToggle ? moduleToggleLabel(pendingModuleToggle.key) : '';

  return (
    <>
      <Dialog open={Boolean(extending)} onOpenChange={(v) => !v && setExtending(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Estender teste grátis</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-fg">
            Estacionamento <strong>{extending?.lotName}</strong>. Quantos dias adicionar?
          </p>
          <div>
            <Label htmlFor="extra-days">Dias extras</Label>
            <Input
              id="extra-days"
              type="number"
              min={1}
              max={365}
              value={extraDays}
              onChange={(e) => setExtraDays(Math.max(1, Number.parseInt(e.target.value, 10) || 1))}
              className="mt-2"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExtending(null)}>
              Cancelar
            </Button>
            <Button
              onClick={() => extending && extendTrial.mutate({ lotId: extending.lotId, extraDays })}
              disabled={extendTrial.isPending}
            >
              <Calendar className="h-4 w-4" /> Estender
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        {...archiveConfirm.dialogProps}
        title="Arquivar estacionamento?"
        description={
          pendingArchive
            ? `${pendingArchive.name} fica oculto na lista e perde acesso. Dá pra desarquivar depois.`
            : 'Esse estacionamento fica oculto na lista.'
        }
        confirmLabel="Arquivar"
        destructive
        pending={archive.isPending}
      />

      <ConfirmDialog
        {...moduleToggleConfirm.dialogProps}
        title={pendingModuleToggle?.enabled ? `Ativar ${modLabel}?` : `Desativar ${modLabel}?`}
        description={pendingModuleToggle ? moduleToggleConfirmDescription(pendingModuleToggle) : ''}
        confirmLabel={pendingModuleToggle?.enabled ? 'Ativar' : 'Desativar'}
        destructive={pendingModuleToggle ? !pendingModuleToggle.enabled : false}
        pending={toggleModule.isPending}
      />

      <ConfirmDialog
        {...unarchiveConfirm.dialogProps}
        title="Desarquivar estacionamento?"
        description={
          pendingUnarchive
            ? `${pendingUnarchive.name} volta a aparecer na lista de ativos e o dono recupera acesso.`
            : 'O estacionamento volta a aparecer na lista de ativos.'
        }
        confirmLabel="Desarquivar"
        pending={unarchive.isPending}
      />

      <ConfirmDialog
        {...resetCheckoutConfirm.dialogProps}
        title="Resetar checkout?"
        description={
          pendingResetCheckout
            ? `Apaga a assinatura atual de ${pendingResetCheckout.name} no provedor e devolve o lote para o estado de teste. O dono pode começar um novo plano em seguida.`
            : 'Apaga a assinatura atual e devolve o lote para o estado de teste.'
        }
        confirmLabel="Resetar"
        destructive
        pending={resetCheckout.isPending}
      />
    </>
  );
};
